/**
 * 강의 관련 API 함수
 */

import type {
  GetCoursesParams,
  GetCoursesResponse,
  CourseDetailDto,
  CourseResponseDto,
  CreateCourseDto,
  ChapterResponseDto,
  LessonDetailDto,
  EnrollmentByCourseDto,
  GetEnrollmentsByCourseParams,
  PaginatedResponseDto,
} from "@repo/types";
import apiClient from "../lib/apiClient";

/**
 * 강의 목록 조회
 *
 * 엔드포인트: GET /api/course
 * - 페이지네이션, 정렬, 필터링 지원
 * - 카테고리별, 강사별 필터링 가능
 *
 * 흐름:
 * 1. 쿼리 파라미터(page, limit, sortBy, sortOrder, filterKey, filterValue 등) 전달
 * 2. GET /course 요청
 * 3. 서버에서 페이지네이션된 강의 목록 반환
 * 4. CourseResponseDto[] 및 메타데이터 반환
 *
 * @param params - 조회 파라미터 (페이지, 정렬, 필터 등)
 * @returns 강의 목록 및 페이지네이션 메타데이터
 * @throws API 요청 실패 시 에러
 */
export const getCourses = async (
  params: GetCoursesParams
): Promise<GetCoursesResponse> => {
  try {
    console.log("📤 강의 목록 API 요청:", {
      url: "/course",
      params,
    });

    const response = await apiClient.get<GetCoursesResponse>("/course", {
      params,
    });

    console.log("📚 강의 목록 조회 성공:", {
      총개수: response.data.meta.total,
      현재페이지: response.data.meta.page,
      데이터개수: response.data.data.length,
    });
    
    // productItemId 포함 여부 확인
    if (response.data.data && response.data.data.length > 0) {
      console.log("🔍 강의 목록 첫 번째 항목 확인:", {
        courseId: response.data.data[0]?.id,
        courseTitle: response.data.data[0]?.title,
        hasProductItemId: 'productItemId' in (response.data.data[0] || {}),
        productItemId: (response.data.data[0] as any)?.productItemId,
        fullCourse: response.data.data[0],
      });
    }
    
    return response.data;
  } catch (error) {
    console.error("❌ 강의 목록 조회 실패:", error);
    throw error;
  }
};

/**
 * 강의 상세 조회
 *
 * 엔드포인트: GET /api/course/:id
 * - 강의 기본 정보 + 카테고리 + 강사 정보
 * - 챕터 목록과 각 챕터의 레슨 목록 포함
 * - 챕터와 레슨은 seq 순서로 정렬됨
 *
 * 흐름:
 * 1. 강의 ID를 path parameter로 전달
 * 2. GET /course/:id 요청
 * 3. 서버에서 강의 상세 정보 반환
 * 4. CourseDetailDto 반환 (챕터 및 레슨 목록 포함)
 *
 * @param courseId - 강의 ID
 * @returns 강의 상세 정보 (챕터, 레슨 포함)
 * @throws API 요청 실패 시 에러
 */
export const getCourseDetail = async (
  courseId: number
): Promise<CourseDetailDto> => {
  try {
    console.log("📤 강의 상세 조회 요청:", { courseId });

    const response = await apiClient.get<CourseDetailDto>(
      `/course/${courseId}`
    );

    console.log("📚 강의 상세 조회 성공:", {
      id: response.data.id,
      title: response.data.title,
      챕터수: response.data.chapters?.length || 0,
      총레슨수: response.data.totalLessonCount,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 강의 상세 조회 실패:", error);
    throw error;
  }
};

/**
 * 강의 수정 요청 DTO
 * - CreateCourseDto의 모든 필드 선택 가능
 * - startAt, endAt은 ISO 8601 형식 문자열로 전송
 */
export type UpdateCourseRequest = Omit<Partial<CreateCourseDto>, "startAt" | "endAt"> & {
  startAt?: string | null;
  endAt?: string | null;
};

/**
 * 강의 생성
 *
 * 엔드포인트: POST /api/course
 * - 필수 필드: kind, categoryId, title, instructorId, totalLessonCount
 * - 선택 필드: level, startAt, endAt, totalDuration, preknowledge
 *
 * @param data - 강의 생성 데이터 (CreateCourseDto)
 * @returns 생성된 강의 정보
 * @throws API 요청 실패 시 에러
 */
export const createCourse = async (
  data: any
): Promise<CourseDetailDto> => {
  try {
    console.log("📤 강의 생성 요청:", data);

    const response = await apiClient.post<CourseDetailDto>("/course", data);

    console.log("✅ 강의 생성 성공:", {
      id: response.data.id,
      title: response.data.title,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 강의 생성 실패:", error);
    throw error;
  }
};

/**
 * 강의 수정
 *
 * 엔드포인트: PATCH /api/course/:id
 * - 부분 업데이트 지원 (전송한 필드만 업데이트)
 * - materialFile이 있으면 FormData로 전송 (강의 자료 업로드)
 *
 * @param courseId - 강의 ID
 * @param data - 강의 수정 데이터 (UpdateCourseRequest)
 * @param materialFile - 강의 자료 파일 (선택, 있으면 multipart로 전송)
 * @returns 수정된 강의 정보 (CourseResponseDto)
 */
export const updateCourse = async (
  courseId: number,
  data: UpdateCourseRequest,
  materialFile?: File
): Promise<CourseResponseDto> => {
  try {
    console.log("📤 강의 수정 요청:", {
      courseId,
      data,
      hasMaterialFile: !!materialFile,
    });

    let response;

    if (materialFile) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined) return;
        // FormData 값은 문자열. null은 빈 문자열로, 나머지는 String(value)
        formData.append(key, value === null ? "" : String(value));
      });
      formData.append("material", materialFile);

      response = await apiClient.patch<CourseResponseDto>(
        `/course/${courseId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } else {
      response = await apiClient.patch<CourseResponseDto>(
        `/course/${courseId}`,
        data
      );
    }

    console.log("✅ 강의 수정 성공:", {
      id: response.data.id,
      title: response.data.title,
    });
    return response.data;
  } catch (error) {
    console.error("❌ 강의 수정 실패:", error);
    throw error;
  }
};

/**
 * 강의 삭제
 *
 * 엔드포인트: DELETE /api/course/:id
 *
 * @param courseId - 강의 ID
 * @throws API 요청 실패 시 에러
 */
export const deleteCourse = async (courseId: number): Promise<void> => {
  try {
    console.log("📤 강의 삭제 요청:", { courseId });

    await apiClient.delete(`/course/${courseId}`);

    console.log("✅ 강의 삭제 성공:", {
      courseId,
    });
  } catch (error) {
    console.error("❌ 강의 삭제 실패:", error);
    throw error;
  }
};

/**
 * 챕터 순서 변경
 *
 * 엔드포인트: PATCH /api/course/:courseId/chapters
 *
 * @param courseId - 강의 ID
 * @param chapterIds - 순서대로 정렬된 챕터 ID 목록
 * @returns 업데이트된 챕터 목록
 */
export const reorderChapters = async (
  courseId: number,
  chapterIds: number[]
): Promise<ChapterResponseDto[]> => {
  try {
    console.log("📤 챕터 순서 변경 요청:", { courseId, chapterIds });

    // chapterIds 배열을 items 형식으로 변환 (seq는 1부터 시작)
    const items = chapterIds.map((id, index) => ({
      id,
      seq: index + 1,
    }));

    const response = await apiClient.patch<ChapterResponseDto[]>(
      `/course/${courseId}/chapters`,
      { items }
    );

    console.log("✅ 챕터 순서 변경 성공");
    return response.data;
  } catch (error) {
    console.error("❌ 챕터 순서 변경 실패:", error);
    throw error;
  }
};

/**
 * 레슨 순서 변경
 *
 * 엔드포인트: PATCH /api/course/chapter/:chapterId/lessons
 *
 * @param chapterId - 챕터 ID
 * @param lessonIds - 순서대로 정렬된 레슨 ID 목록
 * @returns 업데이트된 레슨 목록
 */
export const reorderLessons = async (
  chapterId: number,
  lessonIds: number[]
): Promise<LessonDetailDto[]> => {
  try {
    console.log("📤 레슨 순서 변경 요청:", { chapterId, lessonIds });

    // lessonIds 배열을 items 형식으로 변환 (seq는 1부터 시작)
    const items = lessonIds.map((id, index) => ({
      id,
      seq: index + 1,
    }));

    const response = await apiClient.patch<LessonDetailDto[]>(
      `/course/chapter/${chapterId}/lessons`,
      { items }
    );

    console.log("✅ 레슨 순서 변경 성공");
    return response.data;
  } catch (error) {
    console.error("❌ 레슨 순서 변경 실패:", error);
    throw error;
  }
};

/**
 * 강의별 수강 목록 조회
 *
 * 엔드포인트: GET /api/course/:courseId/enrollments
 * - 페이지네이션 지원
 *
 * 흐름:
 * 1. courseId와 페이지네이션 파라미터 전달
 * 2. GET /course/:courseId/enrollments 요청
 * 3. 서버에서 해당 강의를 수강중인 사용자 목록 반환
 * 4. EnrollmentByProductDto[] 및 메타데이터 반환
 *
 * @param courseId - 강의 ID
 * @param params - 조회 파라미터 (페이지, 제한 등)
 * @returns 수강 목록 및 페이지네이션 메타데이터
 * @throws API 요청 실패 시 에러
 */
export const getCourseEnrollments = async (
  courseId: number,
  params: GetEnrollmentsByCourseParams
): Promise<PaginatedResponseDto<EnrollmentByCourseDto>> => {
  try {
    console.log("📤 강의 수강 목록 조회 요청:", { courseId, params });

    const response = await apiClient.get<PaginatedResponseDto<EnrollmentByCourseDto>>(
      `/course/${courseId}/enrollments`,
      {
        params,
      }
    );

    console.log("✅ 강의 수강 목록 조회 성공:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ 강의 수강 목록 조회 실패:", error);
    throw error;
  }
};
